/**
 * AddSectionPanel - Enhanced left sidebar for Page Builder
 * 
 * Categories:
 * - Layout: Section, Container
 * - Text: Heading, Paragraph, Button
 * - Media: Image, Gallery
 * - Data: SeriesCarousel, NovelCarousel, ArtworkGallery, ContinueReading, TrendingSeries
 * 
 * Each block type includes:
 * - Default props for inspector
 * - Sample data for preview in admin mode
 */
import React, { useState } from 'react';
import { 
  Layout, Type, Image, Grid, Plus, ChevronDown, ChevronRight,
  Layers, AlignLeft, MousePointer, ImageIcon, LayoutGrid,
  BookOpen, Palette, TrendingUp, Clock, Star, Film
} from 'lucide-react';
import { cn } from '../../lib/utils';

// ============================================================================
// BLOCK REGISTRY - All available block types for the Page Builder
// ============================================================================

export const DATA_BLOCK_TYPES = {
  // Series Carousel Row
  series_carousel: {
    id: 'series_carousel',
    type: 'series_carousel',
    name: 'Series Carousel',
    description: 'Horizontal scrolling series cards',
    icon: BookOpen,
    category: 'data',
    defaultProps: {
      title: 'Featured Series',
      contentType: 'series',
      source: 'featured', // trending | new | featured | following | tag | curated
      tag: '',
      limit: 10,
      cardVariant: 'discovery', // reading | discovery | featured
      seeAllLink: '/comics',
      seeAllText: 'See All',
      showRank: false,
      autoScroll: false,
    },
    apiEndpoint: '/homepage/trending',
  },
  
  // Novel Carousel Row
  novel_carousel: {
    id: 'novel_carousel',
    type: 'novel_carousel',
    name: 'Novel Carousel',
    description: 'Horizontal scrolling novel cards',
    icon: BookOpen,
    category: 'data',
    defaultProps: {
      title: 'Featured Novels',
      contentType: 'novel',
      source: 'featured',
      tag: '',
      limit: 10,
      cardVariant: 'discovery',
      seeAllLink: '/novels',
      seeAllText: 'See All',
      showRank: false,
    },
    apiEndpoint: '/homepage/trending?type=novel',
  },
  
  // Artwork Gallery Row
  artwork_gallery: {
    id: 'artwork_gallery',
    type: 'artwork_gallery',
    name: 'Artwork Gallery',
    description: 'Grid or carousel of artwork cards',
    icon: Palette,
    category: 'data',
    defaultProps: {
      title: 'Featured Artworks',
      contentType: 'artwork',
      source: 'featured',
      tag: '',
      limit: 12,
      layout: 'carousel', // carousel | grid
      columns: 4,
      seeAllLink: '/gallery',
      seeAllText: 'View Gallery',
    },
    apiEndpoint: '/featured-artworks',
  },
  
  // Continue Reading Row
  continue_reading: {
    id: 'continue_reading',
    type: 'continue_reading',
    name: 'Continue Reading',
    description: 'Personalized reading progress',
    icon: Clock,
    category: 'data',
    defaultProps: {
      title: 'Continue Reading',
      limit: 6,
      showProgress: true,
      cardVariant: 'reading',
    },
    apiEndpoint: '/user/continue-reading',
    requiresAuth: true,
  },
  
  // Trending Series Row
  trending_series: {
    id: 'trending_series',
    type: 'trending_series',
    name: 'Trending Series',
    description: 'Algorithmically trending content',
    icon: TrendingUp,
    category: 'data',
    defaultProps: {
      title: 'Trending Now',
      contentType: 'series',
      source: 'trending',
      limit: 10,
      cardVariant: 'discovery',
      showRank: true,
      seeAllLink: '/browse?sort=trending',
      seeAllText: 'See All',
    },
    apiEndpoint: '/homepage/trending',
  },
  
  // New Releases Row
  new_releases: {
    id: 'new_releases',
    type: 'new_releases',
    name: 'New Releases',
    description: 'Latest published content',
    icon: Star,
    category: 'data',
    defaultProps: {
      title: 'New Releases',
      contentType: 'series',
      source: 'new',
      limit: 10,
      cardVariant: 'discovery',
      seeAllLink: '/browse?sort=new',
      seeAllText: 'See All',
    },
    apiEndpoint: '/homepage/new-releases',
  },
  
  // Featured Creators Row
  featured_creators: {
    id: 'featured_creators',
    type: 'featured_creators',
    name: 'Featured Creators',
    description: 'Spotlight on creators',
    icon: Star,
    category: 'data',
    defaultProps: {
      title: 'Featured Creators',
      limit: 8,
      cardVariant: 'creator',
      seeAllLink: '/creators',
      seeAllText: 'See All Creators',
    },
    apiEndpoint: '/featured-creators',
  },
};

export const LAYOUT_BLOCK_TYPES = {
  // Section container
  section: {
    id: 'section',
    type: 'section',
    name: 'Section',
    description: 'Full-width container section',
    icon: Layers,
    category: 'layout',
    defaultProps: {
      backgroundColor: 'transparent',
      backgroundImage: '',
      paddingTop: 48,
      paddingBottom: 48,
      maxWidth: '1280px',
    },
  },
  
  // Container
  container: {
    id: 'container',
    type: 'container',
    name: 'Container',
    description: 'Flexible content container',
    icon: Layout,
    category: 'layout',
    defaultProps: {
      maxWidth: '1200px',
      padding: 24,
      alignment: 'center',
      backgroundColor: 'transparent',
    },
  },
};

export const TEXT_BLOCK_TYPES = {
  // Heading
  heading: {
    id: 'heading',
    type: 'heading',
    name: 'Heading',
    description: 'Section heading text',
    icon: Type,
    category: 'text',
    defaultProps: {
      text: 'Section Heading',
      level: 'h2', // h1 | h2 | h3 | h4
      alignment: 'left',
      color: '#ffffff',
      maxWidth: '',
    },
  },
  
  // Paragraph
  paragraph: {
    id: 'paragraph',
    type: 'paragraph',
    name: 'Paragraph',
    description: 'Body text content',
    icon: AlignLeft,
    category: 'text',
    defaultProps: {
      text: 'Add your content here...',
      alignment: 'left',
      color: '#a1a1aa',
      maxWidth: '',
    },
  },
  
  // Button
  button: {
    id: 'button',
    type: 'button',
    name: 'Button',
    description: 'Call-to-action button',
    icon: MousePointer,
    category: 'text',
    defaultProps: {
      text: 'Click Me',
      url: '#',
      variant: 'primary', // primary | secondary | outline | ghost
      size: 'medium', // small | medium | large
      openInNewTab: false,
      alignment: 'left',
    },
  },
};

export const MEDIA_BLOCK_TYPES = {
  // Image
  image: {
    id: 'image',
    type: 'image',
    name: 'Image',
    description: 'Single image block',
    icon: ImageIcon,
    category: 'media',
    defaultProps: {
      src: '',
      alt: '',
      objectFit: 'cover',
      borderRadius: 'md',
      maxHeight: '400px',
      linkUrl: '',
    },
  },
  
  // Gallery Grid
  gallery_grid: {
    id: 'gallery_grid',
    type: 'gallery_grid',
    name: 'Gallery Grid',
    description: 'Multi-image grid layout',
    icon: LayoutGrid,
    category: 'media',
    defaultProps: {
      images: [],
      columns: 3,
      gap: 16,
      aspectRatio: '1:1',
    },
  },
};

// Combine all block types
export const ALL_BLOCK_TYPES = {
  ...LAYOUT_BLOCK_TYPES,
  ...TEXT_BLOCK_TYPES,
  ...MEDIA_BLOCK_TYPES,
  ...DATA_BLOCK_TYPES,
};

// Category definitions
const CATEGORIES = [
  {
    id: 'layout',
    name: 'Layout',
    icon: Layout,
    blocks: Object.values(LAYOUT_BLOCK_TYPES),
  },
  {
    id: 'text',
    name: 'Text',
    icon: Type,
    blocks: Object.values(TEXT_BLOCK_TYPES),
  },
  {
    id: 'media',
    name: 'Media',
    icon: Image,
    blocks: Object.values(MEDIA_BLOCK_TYPES),
  },
  {
    id: 'data',
    name: 'Data Rows',
    icon: Grid,
    blocks: Object.values(DATA_BLOCK_TYPES),
  },
];

// ============================================================================
// AddSectionPanel Component
// ============================================================================

const AddSectionPanel = ({
  onAddBlock,
  currentBlocks = [],
}) => {
  const [expandedCategories, setExpandedCategories] = useState(['data', 'layout']);
  
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };
  
  const handleAddBlock = (blockType) => {
    const blockDef = ALL_BLOCK_TYPES[blockType.id];
    if (!blockDef) return;
    
    // Create new block with unique ID
    const newBlock = {
      id: `${blockType.id}_${Date.now()}`,
      type: blockType.type,
      enabled: true,
      order: currentBlocks.length,
      props: { ...blockType.defaultProps },
      layout: { preset: 'single' },
    };
    
    onAddBlock?.(newBlock);
  };
  
  return (
    <div 
      className="w-[180px] bg-zinc-950 border-r border-zinc-800/30 flex flex-col h-full overflow-hidden"
      data-testid="add-section-panel"
    >
      {/* Header */}
      <div className="px-4 py-4 border-b border-zinc-800/30 flex-shrink-0">
        <h2 className="text-[11px] font-medium text-zinc-400 tracking-wide">
          ADD SECTION
        </h2>
      </div>
      
      {/* Categories */}
      <div className="flex-1 overflow-y-auto py-2">
        {CATEGORIES.map((category) => {
          const isExpanded = expandedCategories.includes(category.id);
          const CategoryIcon = category.icon;
          
          return (
            <div key={category.id} className="mb-1">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category.id)}
                className={cn(
                  "w-full flex items-center gap-2 px-4 py-2",
                  "text-left transition-colors",
                  "hover:bg-zinc-900/50",
                  isExpanded ? "text-zinc-200" : "text-zinc-500"
                )}
                data-testid={`category-${category.id}`}
              >
                {isExpanded ? (
                  <ChevronDown className="w-3 h-3 flex-shrink-0" />
                ) : (
                  <ChevronRight className="w-3 h-3 flex-shrink-0" />
                )}
                <CategoryIcon className="w-4 h-4 flex-shrink-0" />
                <span className="text-[12px] font-medium">{category.name}</span>
              </button>
              
              {/* Block Types */}
              {isExpanded && (
                <div className="pl-4 pr-2 py-1">
                  {category.blocks.map((block) => {
                    const BlockIcon = block.icon;
                    const existsOnPage = currentBlocks.some(b => b.type === block.type);
                    
                    return (
                      <button
                        key={block.id}
                        onClick={() => handleAddBlock(block)}
                        className={cn(
                          "w-full flex items-center gap-2 px-3 py-2 rounded",
                          "text-left transition-all duration-150",
                          "hover:bg-zinc-800/50 group",
                          existsOnPage ? "text-zinc-400" : "text-zinc-500 hover:text-zinc-200"
                        )}
                        title={block.description}
                        data-testid={`add-block-${block.id}`}
                      >
                        <Plus className="w-3 h-3 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                        <BlockIcon className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="text-[11px] truncate">{block.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Quick Add - Data Rows */}
      <div className="p-3 border-t border-zinc-800/30 flex-shrink-0">
        <p className="text-[10px] text-zinc-600 mb-2 uppercase tracking-wider">Quick Add</p>
        <div className="space-y-1">
          <button
            onClick={() => handleAddBlock(DATA_BLOCK_TYPES.series_carousel)}
            className="w-full flex items-center gap-2 px-2 py-1.5 
                       text-[11px] text-zinc-500 hover:text-zinc-200
                       bg-zinc-900/30 hover:bg-zinc-800/50
                       rounded transition-colors"
            data-testid="quick-add-series"
          >
            <BookOpen className="w-3 h-3" />
            <span>+ Series Row</span>
          </button>
          <button
            onClick={() => handleAddBlock(DATA_BLOCK_TYPES.artwork_gallery)}
            className="w-full flex items-center gap-2 px-2 py-1.5 
                       text-[11px] text-zinc-500 hover:text-zinc-200
                       bg-zinc-900/30 hover:bg-zinc-800/50
                       rounded transition-colors"
            data-testid="quick-add-artwork"
          >
            <Palette className="w-3 h-3" />
            <span>+ Artwork Row</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddSectionPanel;
